/**
 * Data Access Service
 * Handles API calls for data scope management and team hierarchy
 */

import apiService from './apiClient';
import logger from '../utils/logger';

const BASE_URL = '/web/master/data-access';

/**
 * Data Scope View Types
 */
export type DataScopeView = 'own' | 'team' | 'all';

/**
 * Accessible User Info
 */
export interface AccessibleUser {
  id: string;
  name: string;
  email: string;
  mobile: string;
  designation: string;
  employeeCode: string;
  userCode: string;
  profilePic: string;
  state: string;
  reportingManagerId: string;
  reportingManagerName: string;
  isDirectReport: boolean;
  level: number;
  subordinateCount?: number;
}

/**
 * Team Hierarchy Node
 */
export interface TeamHierarchyNode {
  id: string;
  name: string;
  email: string;
  mobile: string;
  designation: string;
  employeeCode: string;
  userCode: string;
  profilePic: string;
  state: string;
  status: string;
  level: number;
  children: TeamHierarchyNode[];
  childCount: number;
  totalSubordinates: number;
}

/**
 * Data Scope Info Response
 */
export interface DataScopeInfo {
  userId: string;
  userType: 'sales' | 'backend';
  loginTypeId: number;
  maxScope: DataScopeView;
  dataRights?: string;
  dataAccessType?: string;
  assignedStates?: string[];
  assignedUsers?: string[];
  hasTeamMembers: boolean;
  directReportsCount: number;
  totalTeamSize: number;
}

/**
 * Accessible Users Response
 */
export interface AccessibleUsersResponse {
  users: AccessibleUser[];
  userIds: string[];
  total: number;
  canViewAll: boolean;
  currentScope: DataScopeView;
  maxScope: DataScopeView;
}

/**
 * Data Access Query Parameters
 */
export interface DataAccessQueryParams {
  viewScope?: DataScopeView;
  targetUserId?: string;
  states?: string[];
  directReportsOnly?: boolean;
  includeSelf?: boolean;
  search?: string;
  page?: number;
  limit?: number;
  includeSubordinateCount?: boolean;
}

class DataAccessService {
  /**
   * Get current user's data scope info
   * Returns the user's data access configuration and limits
   */
  async getScopeInfo(): Promise<DataScopeInfo> {
    try {
      logger.debug('DataAccessService.getScopeInfo', 'Fetching data scope info');
      const response = await apiService.get(`${BASE_URL}/scope-info`);
      return response.data;
    } catch (error) {
      logger.error('DataAccessService.getScopeInfo', 'Failed to fetch scope info', error);
      throw error;
    }
  }

  /**
   * Get list of accessible users (for dropdowns and team lists)
   */
  async getAccessibleUsers(params: DataAccessQueryParams = {}): Promise<AccessibleUsersResponse> {
    try {
      logger.debug('DataAccessService.getAccessibleUsers', 'Fetching accessible users', params);

      const queryParams: Record<string, any> = {
        viewScope: params.viewScope || 'team',
        includeSelf: params.includeSelf !== false,
      };

      if (params.targetUserId) queryParams.targetUserId = params.targetUserId;
      if (params.states?.length) queryParams.states = params.states.join(',');
      if (params.directReportsOnly) queryParams.directReportsOnly = true;
      if (params.search) queryParams.search = params.search;
      if (params.page) queryParams.page = params.page;
      if (params.limit) queryParams.limit = params.limit;
      if (params.includeSubordinateCount) queryParams.includeSubordinateCount = true;

      const response = await apiService.get(`${BASE_URL}/accessible-users`, { params: queryParams });

      // Extract from pagination response
      return response.data?.[0] || {
        users: [],
        userIds: [],
        total: 0,
        canViewAll: false,
        currentScope: 'own',
        maxScope: 'own',
      };
    } catch (error) {
      logger.error('DataAccessService.getAccessibleUsers', 'Failed to fetch accessible users', error);
      throw error;
    }
  }

  /**
   * Get direct reports only
   */
  async getDirectReports(): Promise<AccessibleUser[]> {
    try {
      logger.debug('DataAccessService.getDirectReports', 'Fetching direct reports');
      const response = await apiService.get(`${BASE_URL}/direct-reports`);
      return response.data || [];
    } catch (error) {
      logger.error('DataAccessService.getDirectReports', 'Failed to fetch direct reports', error);
      throw error;
    }
  }

  /**
   * Get team hierarchy as tree structure
   */
  async getTeamHierarchy(rootUserId?: string): Promise<TeamHierarchyNode | null> {
    try {
      logger.debug('DataAccessService.getTeamHierarchy', 'Fetching team hierarchy', { rootUserId });
      const params: Record<string, any> = {};
      if (rootUserId) params.rootUserId = rootUserId;

      const response = await apiService.get(`${BASE_URL}/team-hierarchy`, { params });
      return response.data;
    } catch (error) {
      logger.error('DataAccessService.getTeamHierarchy', 'Failed to fetch team hierarchy', error);
      throw error;
    }
  }

  /**
   * Get accessible user IDs only (lightweight)
   */
  async getAccessibleUserIds(params: DataAccessQueryParams = {}): Promise<{ userIds: string[]; total: number }> {
    try {
      logger.debug('DataAccessService.getAccessibleUserIds', 'Fetching user IDs', params);

      const queryParams: Record<string, any> = {
        viewScope: params.viewScope || 'team',
      };

      if (params.targetUserId) queryParams.targetUserId = params.targetUserId;
      if (params.states?.length) queryParams.states = params.states.join(',');
      if (params.directReportsOnly) queryParams.directReportsOnly = true;
      if (params.includeSelf !== undefined) queryParams.includeSelf = params.includeSelf;

      const response = await apiService.get(`${BASE_URL}/user-ids`, { params: queryParams });
      return response.data || { userIds: [], total: 0 };
    } catch (error) {
      logger.error('DataAccessService.getAccessibleUserIds', 'Failed to fetch user IDs', error);
      throw error;
    }
  }

  /**
   * Check if current user can access specific user's data
   */
  async canAccessUserData(targetUserId: string): Promise<{ canAccess: boolean; reason?: string }> {
    try {
      logger.debug('DataAccessService.canAccessUserData', 'Checking access', { targetUserId });
      const response = await apiService.get(`${BASE_URL}/can-access/${targetUserId}`);
      return response.data || { canAccess: false };
    } catch (error) {
      logger.error('DataAccessService.canAccessUserData', 'Failed to check access', error);
      throw error;
    }
  }

  /**
   * Get users by state (for backend users with state access)
   */
  async getUsersByState(states: string[]): Promise<{ userIds: string[]; total: number }> {
    try {
      logger.debug('DataAccessService.getUsersByState', 'Fetching users by state', { states });
      const response = await apiService.get(`${BASE_URL}/users-by-state`, {
        params: { states: states.join(',') },
      });
      return response.data || { userIds: [], total: 0 };
    } catch (error) {
      logger.error('DataAccessService.getUsersByState', 'Failed to fetch users by state', error);
      throw error;
    }
  }
}

export const dataAccessService = new DataAccessService();
export default dataAccessService;
